import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { IArticle } from '@r1-frontend/api-domru/content-static/v1/faq/categories/dto/ResponseDto';

import { CollapsibleBlock } from '@r1-frontend/ui-react/components/CollapsibleBlock';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import ArrowLeft from '@r1-frontend/ui-react/components/svg/arrows/ArrowLeft';
import SingleArrowDown from '@r1-frontend/ui-react/components/svg/arrows/SingleArrowDown';
import SingleArrowUp from '@r1-frontend/ui-react/components/svg/arrows/SingleArrowUp';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import { setFAQActiveArticle, setFAQActiveCategory, setFAQRedirectLoading } from '~/src/features/faq/store/actionCreators';
import { TState } from '~/src/store';

import * as ST from './styled';

interface IProps {
    onChangeMobileView?: (isVisibleCategories: boolean) => void,
}

export const FAQTopicList = ({ onChangeMobileView }: IProps) => {
    const dispatch = useDispatch();
    const router = useRouter();

    const [isMobileViewOpened, setIsMobileViewOpened] = useState(false);
    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);

    const onChangeView = useCallback((isOpened: boolean) => {
        if (onChangeMobileView) {
            onChangeMobileView(isOpened);
        }
        setIsMobileViewOpened(isOpened);
    }, [onChangeMobileView]);

    const {
        categories,
        categoryIds,
        activeCategoryId,
        activeArticle,
    } = useSelector((state: TState) => ({
        categories: state.serviceHelpFAQ.categories,
        categoryIds: state.serviceHelpFAQ.categoryIds,
        activeCategoryId: state.serviceHelpFAQ.activeCategoryId,
        activeArticle: state.serviceHelpFAQ.activeArticle,
    }));

    const [isActiveOpen, setIsActiveOpen] = useState(true);

    if (!categories) {
        return null;
    }

    return <ST.Container>
        <ST.MobileButton
            bType={'primary'}
            onClick={() => onChangeView(!isMobileViewOpened)}
            Icon={isMobileViewOpened ? <SingleArrowUp type={'white'}/> : <SingleArrowDown type={'white'}/>}
        >
            Разделы
        </ST.MobileButton>
        {isMobile && isMobileViewOpened || !isMobile
            ? <ST.TopicListWrap>
                {categoryIds.map((categoryId: string) => {
                    const { articles, ...category } = categories[categoryId];
                    const isActiveCategory = activeCategoryId === category._id;
                    const isActiveCategoryAndOpened = isActiveCategory && isActiveOpen;

                    return <>
                        <ST.Topic>
                            <Link href={isActiveCategory ? router.asPath : `/faq/${category.alias}`}>
                                <ST.Title
                                    isActive={isActiveCategory}
                                    onClick={() => {
                                        if (isActiveCategory) {
                                            setIsActiveOpen(prevIsOpen => !prevIsOpen);
                                        } else {
                                            dispatch(setFAQActiveCategory(category._id));
                                            dispatch(setFAQRedirectLoading(true));
                                            setIsActiveOpen(true);
                                        }
                                    }}
                                >
                                    <ST.Img src={`${category.host}${category.icon}`}/>
                                    <ST.TitleText $withIndent={false}>{category.categoryName}</ST.TitleText>
                                    <ST.ArrowUp isOpen={isActiveCategoryAndOpened}/>
                                </ST.Title>
                            </Link>
                        </ST.Topic>
                        <CollapsibleBlock key={category._id} isOpen={isActiveCategoryAndOpened}>
                        <ST.Articles>
                            {articles.map((article: IArticle) =>
                                <Link href={`/faq/${category.alias}/${article.alias}`} key={article._id}>
                                    <ST.SubTitle
                                        key={article.alias}
                                        isActive={activeArticle === article.alias}
                                        onClick={() => {
                                            dispatch(setFAQActiveArticle(article.alias));
                                            dispatch(setFAQRedirectLoading(true));
                                        }}
                                    >
                                        {article.articleTitle}
                                    </ST.SubTitle>
                                </Link>)
                            }
                        </ST.Articles>
                    </CollapsibleBlock>
                    </>;
                })}
            </ ST.TopicListWrap>
            : <ST.ButtonBack
                animation={false}
                bType={'ghost'}
                Icon={<ArrowLeft/>}
                onClick={() => {
                    dispatch(setFAQRedirectLoading(true));
                    router.push('/faq');
                }}
            >
                Назад
            </ST.ButtonBack>}
    </ST.Container>;
};
